/* assets/reserve.js
   ---------------------------------------------------------------------------
   Scoped behavior for the Soopl "reserve" campaign page.
   Loaded ONLY by layout/reserve.liquid. Namespaced to avoid global collisions.
   No dependencies. See CLAUDE.md → HARD CONSTRAINT before editing.
   ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var Reserve = {
    init: function () {
      this.initCountdown();
      this.initForms();
      this.initLeadTracking();
    },

    /* Countdown
       Drives every [data-reserve-countdown] block rendered by
       snippets/reserve-countdown.liquid. The target is epoch SECONDS in
       [data-launch-timestamp] (resolved server-side in Liquid), so the
       countdown lands on the same instant for every visitor whatever their
       own clock says. No-ops when the snippet isn't on the page. */
    initCountdown: function () {
      var blocks = document.querySelectorAll('[data-reserve-countdown]');
      Array.prototype.forEach.call(blocks, function (block) {
        Reserve.startCountdown(block);
      });
    },

    startCountdown: function (block) {
      var seconds = parseInt(block.getAttribute('data-launch-timestamp'), 10);
      if (!seconds || isNaN(seconds)) return;

      var target = seconds * 1000;
      var timer = block.querySelector('.reserve-countdown__timer');
      var message = block.querySelector('[data-reserve-countdown-message]');
      var units = {};
      Array.prototype.forEach.call(
        block.querySelectorAll('[data-reserve-countdown-unit]'),
        function (el) { units[el.getAttribute('data-reserve-countdown-unit')] = el; }
      );
      if (!timer) return;

      var animate = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function makeDigit() {
        var digit = document.createElement('span');
        digit.className = 'reserve-countdown__digit';
        digit.innerHTML =
          '<span class="reserve-countdown__num reserve-countdown__num--current">0</span>' +
          '<span class="reserve-countdown__num reserve-countdown__num--previous" aria-hidden="true">0</span>';
        return digit;
      }

      function setDigit(digit, value) {
        var current = digit.querySelector('.reserve-countdown__num--current');
        var previous = digit.querySelector('.reserve-countdown__num--previous');
        if (!current || current.textContent === value) return;

        if (!animate) {
          current.textContent = value;
          return;
        }

        // The outgoing digit becomes the "previous" layer, both snap to the
        // pre-animation state, then a forced reflow commits it so removing the
        // class actually transitions instead of jumping.
        previous.textContent = current.textContent;
        current.textContent = value;
        current.classList.add('is-animating');
        previous.classList.add('is-animating');
        void digit.offsetHeight;
        current.classList.remove('is-animating');
        previous.classList.remove('is-animating');
      }

      // Days is the only unit that can outgrow its two tiles (100+ days out),
      // so the tile count follows the string — never dropping below two.
      function render(el, value) {
        if (!el) return;
        var text = String(value);
        if (text.length < 2) text = '0' + text;

        while (el.children.length < text.length) el.appendChild(makeDigit());
        while (el.children.length > text.length && el.children.length > 2) {
          el.removeChild(el.lastElementChild);
        }
        for (var i = 0; i < text.length; i++) setDigit(el.children[i], text.charAt(i));
      }

      function expire() {
        if (message) {
          timer.hidden = true;
          message.hidden = false;
        } else {
          block.hidden = true;
        }
        block.setAttribute('data-reserve-countdown-expired', '');
      }

      function tick() {
        var diff = target - Date.now();

        if (diff <= 0) {
          render(units.days, 0);
          render(units.hours, 0);
          render(units.minutes, 0);
          render(units.seconds, 0);
          block.setAttribute('data-reserve-countdown-ready', '');
          expire();
          return;
        }

        render(units.days, Math.floor(diff / 86400000));
        render(units.hours, Math.floor((diff % 86400000) / 3600000));
        render(units.minutes, Math.floor((diff % 3600000) / 60000));
        render(units.seconds, Math.floor((diff % 60000) / 1000));
        block.setAttribute('data-reserve-countdown-ready', '');

        // Re-align to the next real second instead of setInterval(1000), which
        // drifts and skips digits when the tab is throttled in the background.
        window.setTimeout(tick, 1000 - (Date.now() % 1000));
      }

      tick();
    },

    /* Email capture
       Forms are native Shopify customer forms ({% form 'customer' %}) — the
       POST creates the customer with marketing consent and the "leads" tag.
       JS only validates before letting the native submit proceed; success
       and error states render server-side via form.posted_successfully? */
    initForms: function () {
      var forms = document.querySelectorAll('[data-reserve-form]');
      Array.prototype.forEach.call(forms, function (form) {
        var rememberSource = function () {
          try {
            window.sessionStorage.setItem(
              'reserve-lead-source',
              form.id === 'reserve-footer-signup' ? 'footer' : 'hero'
            );
          } catch (err) { /* storage blocked */ }
        };

        // Capture the source at CLICK time as well as submit time: form-grabbing
        // browser extensions (HubSpot collector etc.) and captcha flows may
        // cancel the submit and re-submit via form.submit(), which skips submit
        // listeners entirely — but the click always happened.
        var btn = form.querySelector('button[type="submit"]');
        if (btn) btn.addEventListener('click', rememberSource);

        form.addEventListener('submit', function (e) {
          var email = form.querySelector('input[type="email"]');
          var note = form.querySelector('[data-reserve-form-note]');

          if (email && !email.checkValidity()) {
            e.preventDefault();
            email.setAttribute('aria-invalid', 'true');
            if (note) note.textContent = 'Please enter a valid email address.';
            email.focus();
            return;
          }

          if (email) email.removeAttribute('aria-invalid');
          rememberSource();
          // Valid: allow the native submit through to Shopify.
        });
      });
    },

    /* Lead tracking + post-signup forward to the deposit PDP
       After a successful signup Shopify redirects back with
       ?customer_posted=true. Fire ONE dataLayer event there (GTM turns it
       into the Meta pixel Lead), THEN forward to the $1 deposit product page
       (August 2026 — signup should hand straight off to the deposit).

       Why the two-hop instead of pointing return_to at the PDP directly:
       GTM (GTM-M9P8ZJM) is loaded by layout/reserve.liquid ONLY — theme.liquid
       has no container — so a direct redirect would leave the Lead event with
       nowhere to fire. The forward waits on GTM's eventCallback so the tags
       for lead_submitted have actually fired before we navigate; eventTimeout
       plus a hard setTimeout guarantee we leave even if GTM is slow, blocked
       by an ad blocker, or never loads at all.

       The destination comes from the form's data-reserve-signup-redirect
       attribute (rendered via snippets/reserve-checkout-url so it localizes
       per market), falling back to the canonical PDP path.

       Both forms render their success state on
       that view (same form type), so the event must not live in the section
       markup — it would double-fire.

       Dedup hardening (July 2026, after Meta reported ~3.6x real signups):
       - The done flag lives in localStorage, so the Lead fires at most once
         per BROWSER, not per session — revisits of a bookmarked/shared
         success URL in a later session no longer re-fire. (Tradeoff: a
         genuine second signup from the same browser is not re-counted.)
       - The customer_posted flag is stripped from the URL via replaceState
         once handled. Shopify evaluates form.posted_successfully? from the
         flag at PAGE LOAD (server-side), so the success view is untouched;
         reloads/bookmarks/shares of the cleaned URL simply render the
         normal page. */
    initLeadTracking: function () {
      if (window.location.search.indexOf('customer_posted=true') === -1) return;

      // Where to send the customer once the lead event has fired.
      var host = document.querySelector('[data-reserve-signup-redirect]');
      var destination = host && host.getAttribute('data-reserve-signup-redirect');
      if (!destination) destination = '/products/soopl-cabin-reservation';

      // replace() so the success URL never becomes a back-button target.
      var forwarded = false;
      var forward = function () {
        if (forwarded) return;
        forwarded = true;
        window.location.replace(destination);
      };

      var stripFlag = function () {
        if (!window.history || !window.history.replaceState) return;
        var search = window.location.search
          .replace(/([?&])customer_posted=true&?/, '$1')
          .replace(/[?&]$/, '');
        window.history.replaceState(
          null, '', window.location.pathname + search + window.location.hash
        );
      };

      var session, local;
      try {
        session = window.sessionStorage;
        local = window.localStorage;
        local.getItem('reserve-lead-done');
      } catch (err) {
        // Storage blocked — skip the event rather than risk double-counting,
        // but still hand the customer off to the deposit page.
        stripFlag();
        forward();
        return;
      }

      var source = session.getItem('reserve-lead-source');
      session.removeItem('reserve-lead-source');

      if (local.getItem('reserve-lead-done')) {
        stripFlag();
        forward();
        return;
      }
      if (!source) {
        // No submit-time key: an interceptor may have re-submitted the form
        // outside our listeners. Count the lead once anyway.
        source = 'unknown';
      }
      local.setItem('reserve-lead-done', '1');

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'lead_submitted',
        form_location: source,
        // GTM calls eventCallback once every tag bound to lead_submitted has
        // fired, and honours eventTimeout if one of them hangs.
        eventCallback: forward,
        eventTimeout: 1200
      });
      stripFlag();

      // Backstop: if GTM itself never loads (ad blocker, network failure)
      // eventCallback is never invoked, so leave on our own.
      window.setTimeout(forward, 1500);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { Reserve.init(); });
  } else {
    Reserve.init();
  }
})();
